import { erpApi } from "@/shared/axios";
import { urlBackend } from "@/shared/constants";
import { AlertSeverityEnum, UseMutationsParams } from "@/shared/interfaces";
import { SolicitudExtranjero, SolicitudExtranjeroFrom, SolicitudExtranjeroParams, SolicitudExtranjeroResp } from "@/shared/interfaces/erp/solicitud_extranjero/solicitud_extranjero.interface";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { useUiStore } from "@/store/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UpdatePreagendamientoParams } from "../operaciones";

const { get, post, put } = erpApi();

///* Obetener todas las solicitudes de extranjero

export const useFetchSolicitudExtranjero = (params: SolicitudExtranjeroParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['solicitudextrangeroprospecto', ...Object.values(params || {})],
    queryFn: async () => {
      return await getSolicitudExtranjero(params);
    },
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getSolicitudExtranjero = async (params?: SolicitudExtranjeroParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<SolicitudExtranjeroResp>(
    `${urlBackend}api/v1/solicitudextrangeroprospecto/?${queryParams}`,
    true
  );
  return resp;
};

///* Crear una solicitud de extranjero

export const usePostSolicitudExtranjero = ({ }: UseMutationsParams) => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: postSolicitudExtranjero,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['solicitudextrangeroprospecto'] });
      setAlert(true, data.message, data.status);
    },

    onError: (e => {
      setIsGlobalLoading(false);
      handleAxiosError(e);
    }),
  });
};

export const postSolicitudExtranjero = async (data: SolicitudExtranjeroFrom) => {
  const res = await post<SolicitudExtranjero>(`${urlBackend}api/v1/solicitudextrangeroprospecto/`, data, true);
  return res;
};

///* Editar una solicitud de extranjero
export const usePutSolicitudExtranjero = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,

  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdatePreagendamientoParams<T>) =>
      putSolicitudExtranjero(params),

    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['solicitudextrangeroprospecto'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Preagendamiento actualizada correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const putSolicitudExtranjero = async <T>({
  id,
  data,
}: UpdatePreagendamientoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<SolicitudExtranjero>(
    `${urlBackend}api/v1/solicitudextrangeroprospecto/${id}/`,
    data,
    true
  );
};


///* Buscar una solicitud extranjero por su uid

export const useGetSolicitudExtranjeroById = (id: string, enabled = false) => {
  return useQuery({
    queryKey: ['solicitudextrangeroprospecto', id],
    queryFn: async () => {
      return getSolicitudExtranjeroById(id);
    },
    enabled,
    refetchOnWindowFocus: false
  });
};

export const getSolicitudExtranjeroById = async (id: string) => {
  const res = await get<SolicitudExtranjero>(`${urlBackend}api/v1/solicitudextrangeroprospecto/${id}`, true);

  return res.data;
};
